import { isAxiosError } from 'axios';

/**
 * Von der API abgelehnte Eingaben **am Feld** anzeigen, nicht als „Ungültige Angaben".
 *
 * ### Warum das hier steht und nicht im Formular
 *
 * Jeder schreibende Endpunkt antwortet bei einem Validierungsfehler nach demselben Muster:
 * `400` mit `{ code: 'VALIDATION_ERROR', message, issues: ZodIssue[] }`. Die `issues` tragen
 * den Pfad des verantwortlichen Feldes — genau die Information, die eine Sammelmeldung
 * wegwirft. Ein Lieferant, der im Wizard auf „Weiter" klickt und „Ungültige Angaben" liest,
 * weiß nicht, ob die Postleitzahl vier Ziffern hat oder die Website ohne Punkt eingetragen
 * ist; er probiert, findet nichts und hört auf.
 *
 * Die Auswertung liegt in `api-client`, weil Lieferantenportal **und** Admin-App dieselben
 * Antworten bekommen. Zwei Kopien wären zwei Auslegungen desselben Fehlerformats — genau der
 * Fall, den D11 für den Auth-Interceptor ausschließt.
 *
 * ### Der Zod-Teil
 *
 * `issues` ist bewusst nicht als Zod-Typ eingebunden: Es ist eine **JSON-Antwort**, kein
 * Zod-Objekt, und es kommt von einem Server, dessen Version nicht die des Clients sein muss.
 * Was hier geprüft wird, ist deshalb die Form, nicht der Typ.
 */

/** Fehlermeldungen je Feldname. Der Schlüssel ist die `id` des Eingabefeldes. */
export type FieldErrors = Record<string, string>;

/**
 * Schlüssel für Fehler, die zu keinem einzelnen Feld gehören („die Kombination stimmt
 * nicht"). Formulare zeigen ihn über der Feldliste an.
 */
export const FORM_ERROR_KEY = '_form';

/** Eine Zod-Beanstandung, so wie sie über JSON ankommt. */
export interface ApiIssue {
  path: (string | number)[];
  message: string;
}

function isApiIssue(value: unknown): value is ApiIssue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return Array.isArray(candidate.path) && typeof candidate.message === 'string';
}

/**
 * Zieht die Beanstandungen aus einer abgelehnten API-Antwort.
 *
 * Gibt eine leere Liste zurück, wenn es keine gibt — ein 500er oder ein Netzwerkabbruch ist
 * kein Formularfehler und darf keine Felder rot färben.
 */
export function issuesFromApiError(error: unknown): ApiIssue[] {
  if (!isAxiosError(error)) return [];
  const data = error.response?.data as { issues?: unknown } | undefined;
  if (!Array.isArray(data?.issues)) return [];
  return data.issues.filter(isApiIssue);
}

/** Die Meldung aus einer Fehlerantwort, oder `undefined`, wenn keine mitkam. */
export function messageFromApiError(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as { message?: unknown } | undefined;
  return typeof data?.message === 'string' ? data.message : undefined;
}

/**
 * Beanstandungen → Feldfehler.
 *
 * Der Pfad wird punktiert zusammengesetzt (`address.postalCode`) und über `fieldByPath`
 * einem Formularfeld zugeordnet. Steht der Pfad nicht in der Zuordnung, greift das **letzte
 * Pfadsegment** als Feldname — in der Praxis heißt das Eingabefeld genauso wie das
 * Schemafeld, und die Zuordnung bleibt auf die Ausnahmen beschränkt (etwa eine
 * Tages-Eingabe, hinter der ein Stundenwert steht).
 *
 * Der **erste** Fehler je Feld gewinnt. Zwei Meldungen unter einem Eingabefeld sind eine
 * Meldung zu viel; die zweite steht ohnehin erst wieder an, wenn die erste behoben ist.
 */
export function toFieldErrors(issues: readonly ApiIssue[], fieldByPath: Record<string, string> = {}): FieldErrors {
  const errors: FieldErrors = {};

  for (const issue of issues) {
    const path = issue.path.join('.');
    const last = issue.path.at(-1);
    const field =
      // eslint-disable-next-line security/detect-object-injection -- `path` ist aus der Antwort zusammengesetzt, `fieldByPath` ein Literal ohne Prototypen-Schlüssel
      fieldByPath[path] ?? (path === '' ? FORM_ERROR_KEY : typeof last === 'string' ? last : FORM_ERROR_KEY);

    // eslint-disable-next-line security/detect-object-injection -- siehe oben; der Wert wird nur gelesen und gesetzt, nie ausgeführt
    if (errors[field] === undefined) errors[field] = issue.message;
  }

  return errors;
}

/**
 * Springt zum ersten fehlerhaften Feld und setzt den Fokus.
 *
 * Auf dem Handy — und dort wird dieses Portal überwiegend ausgefüllt — steht der Fehler
 * sonst außerhalb des Bildschirms, während der Speichern-Knopf unten sichtbar bleibt. Der
 * Nutzer sieht dann nur, dass nichts passiert.
 */
export function focusFirstError(errors: FieldErrors): void {
  const first = Object.keys(errors).find((key) => key !== FORM_ERROR_KEY);
  if (first === undefined || typeof document === 'undefined') return;

  const element = document.getElementById(first);
  if (!(element instanceof HTMLElement)) return;

  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  element.focus({ preventScroll: true });
}
